import React from 'react'
import { useTranslate } from '@/utils/translate/translate-client'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsTooltip } from './HlsTooltip'

/**
 * Enter fullscreen icon — four corner brackets using stroke paths.
 * Wider horizontal spacing between left and right corners.
 */
const EnterFullscreenIcon: React.FC = () => (
  <svg width={28} height={24} viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Top-left corner */}
    <path d="M4 9V4H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Top-right corner */}
    <path d="M23 4H28V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Bottom-left corner */}
    <path d="M9 20H4V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Bottom-right corner */}
    <path d="M28 15V20H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Exit fullscreen icon — four inward-pointing corner brackets using stroke paths.
 */
const ExitFullscreenIcon: React.FC = () => (
  <svg width={28} height={24} viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Top-left corner (points inward) */}
    <path d="M4 9H9V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Top-right corner (points inward) */}
    <path d="M23 4V9H28" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Bottom-left corner (points inward) */}
    <path d="M9 20V15H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    {/* Bottom-right corner (points inward) */}
    <path d="M28 15H23V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

/**
 * Fullscreen toggle button for the control bar.
 * Enters fullscreen on the player container (not just the video element)
 * so that custom UI controls remain visible in fullscreen mode.
 */
export const HlsFullscreenButton: React.FC = () => {
  const { fullscreen, controller } = useHlsPlayerUI()
  const { t } = useTranslate('common')

  const handleClick = () => {
    if (!controller) return
    if (fullscreen) {
      controller.exitFullscreen()
    } else {
      controller.enterFullscreen()
    }
  }

  return (
    <button
      type="button"
      className="group relative flex h-9 w-9 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20"
      onClick={handleClick}
      aria-label={fullscreen ? t('exitFullscreen', 'Exit Fullscreen') : t('fullscreen', 'Fullscreen')}
    >
      {fullscreen ? <ExitFullscreenIcon /> : <EnterFullscreenIcon />}
      <HlsTooltip
        title={fullscreen ? t('exitFullscreen', 'Exit Fullscreen') : t('fullscreen', 'Fullscreen')}
        subtitle={
          fullscreen
            ? t('exitFullscreenDescription', 'Return to normal view.')
            : t('fullscreenDescription', 'Expand for a bigger picture.')
        }
      />
    </button>
  )
}
